import { useEffect, useRef, useCallback } from "react";
import type { ReactNode } from "react";
import { LayoutGroup, motion } from "framer-motion";
import { Button } from "./button";

type ModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({ open, title, onClose, children, actions }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const trapFocus = useCallback((event: KeyboardEvent) => {
    if (event.key !== "Tab") return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      trapFocus(event);
    };

    window.addEventListener("keydown", onKeyDown);

    requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (dialog) {
        const firstFocusable = dialog.querySelector<HTMLElement>(FOCUSABLE);
        if (firstFocusable) firstFocusable.focus();
        else dialog.focus();
      }
    });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [open, onClose, trapFocus]);

  if (!open) return null;

  return (
    <div className="cf-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={dialogRef}
        className="cf-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <LayoutGroup id="modal-layout">
          <motion.div className="cf-grid" layout initial={false}>
            <h3
              style={{
                borderBottom: "2px solid var(--cf-brand)",
                paddingBottom: "var(--cf-space-sm)",
              }}
            >
              {title}
            </h3>
            <motion.div data-testid="cf-modal-body" layout style={{ overflow: "hidden" }}>
              {children}
            </motion.div>
            <motion.div data-testid="cf-modal-actions" className="cf-status-row" layout>
              <div>{actions}</div>
              <Button type="button" variant="ghost" tone="neutral" onClick={onClose}>
                Close
              </Button>
            </motion.div>
          </motion.div>
        </LayoutGroup>
      </div>
    </div>
  );
}
